import React from 'react';
import { useTemplateProvider } from '../template-provider';
import { PageTemplate } from '../../utils/match-page-template';

interface ICtaContext {
    hideNationalCallCenter?: boolean
    hideFranchiseLink?: boolean
    overridePhoneNumber?: string
    overridePhoneNumberDescriptor?: string,
}

const CtaContext = React.createContext<ICtaContext>(null as unknown as ICtaContext);

type CtaProviderProps = {
    children: React.ReactNode | React.ReactNode[] | null | string
    data?: unknown
} & ICtaContext;

const CtaProvider = ({
    children,
    data,
}: CtaProviderProps): JSX.Element => {
    const { template } = useTemplateProvider();

    const {
        hideFranchiseLink,
        hideNationalCallCenter,
        overridePhoneNumber,
        overridePhoneNumberDescriptor,
    } = React.useMemo(() => {
    // Handle templates that can never have a page option
        if (
            template === PageTemplate.LocationsIndex
          || template === PageTemplate.MetroState
          || template === PageTemplate.NotFoundPage
        ) {
            return {
                hideNationalCallCenter: false,
                hideFranchiseLink: true,
                overridePhoneNumber: undefined,
                overridePhoneNumberDescriptor: undefined,
            };
        }

        if (template === PageTemplate.Metro) {
            return {
                hideNationalCallCenter: true,
                hideFranchiseLink: true,
                overridePhoneNumber: undefined,
                overridePhoneNumberDescriptor: undefined,
            };
        }

        // Try to find pageOptions in the data structure
        const findPageOptions = (obj: unknown): Record<string, unknown> | null => {
            if (!obj || typeof obj !== 'object') return null;

            // Check if this object has pageOptions
            if ('pageOptions' in obj && obj.pageOptions && typeof obj.pageOptions === 'object') {
                return obj.pageOptions as Record<string, unknown>;
            }

            // Recursively search through object values
            return Object.values(obj).reduce<Record<string, unknown> | null>((acc, value) => {
                if (acc) return acc;
                if (typeof value === 'object' && value !== null) {
                    return findPageOptions(value);
                }
                return acc;
            }, null);
        };

        const pageOptions = findPageOptions(data);

        return {
            hideNationalCallCenter: pageOptions?.hideNationalCallCenter as boolean | undefined,
            hideFranchiseLink: pageOptions?.hideLocalCta as boolean | undefined,
            overridePhoneNumber: pageOptions?.overridePhoneNumber as string | undefined,
            overridePhoneNumberDescriptor: pageOptions?.overridePhoneNumberDescriptor as string | undefined,
        };
    }, [data, template]);

    const value = React.useMemo(() => ({
        hideFranchiseLink,
        hideNationalCallCenter,
        overridePhoneNumber,
        overridePhoneNumberDescriptor,
    }), [
        hideFranchiseLink,
        hideNationalCallCenter,
        overridePhoneNumber,
        overridePhoneNumberDescriptor,
    ]);

    return (
        <CtaContext.Provider
            value={value}
        >
            {children}
        </CtaContext.Provider>
    );
};

export default CtaProvider;

export const useCtaProvider = (): ICtaContext => {
    const ctx = React.useContext(CtaContext);
    if (!ctx) {
        throw new Error('No CTA Context has been provided.');
    }
    return ctx;
};
